import type { CampaignsStore } from './index';

type CampaignsFilterKey =
  | 'advertiserListFilters'
  | 'campaignListFilters'
  | 'deviatedLineItemsListFilters'
  | 'ioListFilters'
  | 'lineItemsListFilters';

export type PersistedCampaignsFilters = Partial<Pick<CampaignsStore, CampaignsFilterKey>>;

type PersistedCampaignsFiltersByUser = Record<string, PersistedCampaignsFilters>;

const STORAGE_KEY = 'campaignsListFilters';

const FILTER_KEYS: CampaignsFilterKey[] = [
  'advertiserListFilters',
  'campaignListFilters',
  'deviatedLineItemsListFilters',
  'ioListFilters',
  'lineItemsListFilters'
];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isPersistedCampaignsFilters = (value: unknown): value is PersistedCampaignsFilters => {
  if (!isPlainObject(value)) {
    return false;
  }
  return Object.entries(value).every(
    ([key, filterValue]) => (FILTER_KEYS as string[]).includes(key) && isPlainObject(filterValue)
  );
};

const isPersistedCampaignsFiltersByUser = (value: unknown): value is PersistedCampaignsFiltersByUser => {
  if (!isPlainObject(value)) {
    return false;
  }
  return Object.values(value).every(isPersistedCampaignsFilters);
};

const readPersistedCampaignsFilters = (): PersistedCampaignsFiltersByUser => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    return isPersistedCampaignsFiltersByUser(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

export const getPersistedCampaignsFiltersForUser = (userId: string): PersistedCampaignsFilters => {
  return readPersistedCampaignsFilters()[userId] ?? {};
};

export const persistCampaignsFilter = <K extends CampaignsFilterKey>(
  userId: string,
  filterKey: K,
  filterValue: CampaignsStore[K]
): void => {
  const allFilters = readPersistedCampaignsFilters();
  const updatedFilters: PersistedCampaignsFiltersByUser = {
    ...allFilters,
    [userId]: {
      ...allFilters[userId],
      [filterKey]: filterValue
    }
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedFilters));
  } catch {
    // Persistence is a non-critical enhancement — fail silently if storage is unavailable.
  }
};
